/**
 * LabPanel — the Phase 1 species-lab console: seed entry, spin/randomise
 * controls, and the ten per-species readouts that make the < 900-triangle
 * acceptance gate visible at a glance.
 */

import { hashString } from '../core/Rng.ts';
import { PERF } from '../data/tuning.ts';

export interface LabCardState {
  id: string; name: string; triangles: number; voxels: number; ms: number; over: boolean;
}

/** Up to 10 digits is the widest decimal that can still be a uint32. */
const DIGITS = /^\d{1,10}$/;
const U32 = 0xFFFFFFFF;

function groupInt(n: number): string {
  const s = String(n);
  if (s.length <= 3) return s;
  let out = '';
  for (let i = s.length, c = 0; i > 0; i--) {
    out = s.charAt(i - 1) + out;
    if (++c % 3 === 0 && i > 1) out = ',' + out;
  }
  return out;
}

function mk<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls: string, parent?: HTMLElement,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  if (parent) parent.appendChild(e);
  return e;
}

interface Card {
  root: HTMLDivElement;
  name: HTMLSpanElement;
  tri: HTMLSpanElement;
  sub: HTMLSpanElement;
  nameCache: string;
  triCache: string;
  subCache: string;
  over: boolean;
  hidden: boolean;
}

export class LabPanel {
  onSeedChange: (seed: number) => void = () => {};
  onRandomize: () => void = () => {};
  onToggleSpin: (on: boolean) => void = () => {};

  private readonly root: HTMLElement;
  private readonly cardsEl: HTMLDivElement;
  private readonly seedEl: HTMLInputElement;
  private readonly randomEl: HTMLButtonElement;
  private readonly spinEl: HTMLButtonElement;
  private readonly flagEl: HTMLSpanElement;
  private readonly statusEl: HTMLDivElement;

  private readonly cards: Card[] = [];
  /** Last string handed to onSeedChange; suppresses the keydown/change double-fire. */
  private committed = '';
  private statusCache = '';
  private flagCache = -1;
  /** The lab turntable starts running — SPEC §7 shows the species as a live carousel. */
  private spin = true;

  private readonly onSeedKey: (e: KeyboardEvent) => void;
  private readonly onSeedCommit: () => void;
  private readonly onRandomClick: () => void;
  private readonly onSpinClick: () => void;

  constructor(parent: HTMLElement) {
    this.root = mk('section', 'lab', parent);
    const shell = mk('div', 'lab-shell', this.root);
    this.cardsEl = mk('div', 'lab-cards', shell);

    const bar = mk('div', 'lab-bar', shell);
    const group = mk('div', 'lab-group', bar);

    const label = mk('label', 'lab-label', group);
    label.textContent = 'SEED';
    label.htmlFor = 'lab-seed';

    this.seedEl = mk('input', 'lab-seed', group);
    this.seedEl.id = 'lab-seed';
    this.seedEl.type = 'text';
    this.seedEl.spellcheck = false;
    this.seedEl.autocomplete = 'off';
    this.seedEl.placeholder = 'NUMBER OR WORD';

    this.randomEl = mk('button', 'lab-btn', group);
    this.randomEl.type = 'button';
    this.randomEl.textContent = 'RANDOM';

    this.spinEl = mk('button', 'lab-btn lab-tog is-on', group);
    this.spinEl.type = 'button';
    this.spinEl.textContent = 'SPIN';
    this.spinEl.setAttribute('aria-pressed', 'true');

    this.flagEl = mk('span', 'lab-flag is-hidden', group);
    this.statusEl = mk('div', 'lab-status', bar);

    this.onSeedKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      this.commitSeed();
      /* Hand the keyboard back to the game: Input ignores keys only while a
         text field owns focus, so leaving it focused would eat WASD. */
      this.seedEl.blur();
    };
    this.onSeedCommit = () => { this.commitSeed(); };
    /* Both buttons blur themselves: a focused button swallows Space (lunge)
       and would re-fire on the next press. */
    this.onRandomClick = () => { this.randomEl.blur(); this.onRandomize(); };
    this.onSpinClick = () => {
      this.spinEl.blur();
      this.spin = !this.spin;
      this.spinEl.classList.toggle('is-on', this.spin);
      this.spinEl.setAttribute('aria-pressed', this.spin ? 'true' : 'false');
      this.onToggleSpin(this.spin);
    };

    this.seedEl.addEventListener('keydown', this.onSeedKey);
    this.seedEl.addEventListener('change', this.onSeedCommit);
    this.randomEl.addEventListener('click', this.onRandomClick);
    this.spinEl.addEventListener('click', this.onSpinClick);
  }

  setSeed(seed: number): void {
    const s = String(seed >>> 0);
    this.committed = s;
    if (this.seedEl.value !== s) this.seedEl.value = s;
  }

  setCards(cards: LabCardState[]): void {
    let over = 0;
    for (let i = 0; i < cards.length; i++) {
      const c = cards[i];
      const card = this.card(i);
      if (card.hidden) { card.hidden = false; card.root.style.display = ''; }

      if (card.nameCache !== c.name) {
        card.nameCache = c.name;
        card.name.textContent = c.name;
        card.root.dataset.species = c.id;
      }
      const tri = groupInt(c.triangles | 0);
      if (card.triCache !== tri) { card.triCache = tri; card.tri.textContent = tri; }

      const sub = groupInt(c.voxels | 0) + ' VOX · ' + c.ms.toFixed(1) + ' MS';
      if (card.subCache !== sub) { card.subCache = sub; card.sub.textContent = sub; }

      /* Trust the caller's flag, but the budget is the contract — recompute so a
         stale `over` can never hide a failing shark. */
      const isOver = c.over || c.triangles > PERF.maxTriangleBudget;
      if (card.over !== isOver) {
        card.over = isOver;
        card.root.classList.toggle('is-over', isOver);
      }
      if (isOver) over++;
    }
    for (let i = cards.length; i < this.cards.length; i++) {
      const card = this.cards[i];
      if (!card.hidden) { card.hidden = true; card.root.style.display = 'none'; }
    }

    if (over !== this.flagCache) {
      this.flagCache = over;
      this.flagEl.classList.toggle('is-hidden', over === 0);
      if (over > 0) {
        this.flagEl.textContent = over + ' OVER ' + PERF.maxTriangleBudget + ' TRI';
      }
    }
  }

  setStatus(text: string): void {
    if (text === this.statusCache) return;
    this.statusCache = text;
    this.statusEl.textContent = text;
  }

  dispose(): void {
    this.seedEl.removeEventListener('keydown', this.onSeedKey);
    this.seedEl.removeEventListener('change', this.onSeedCommit);
    this.randomEl.removeEventListener('click', this.onRandomClick);
    this.spinEl.removeEventListener('click', this.onSpinClick);
    this.root.remove();
    this.cards.length = 0;
  }

  /** Cards are pooled, never rebuilt — `setCards` runs on every lab rebuild. */
  private card(i: number): Card {
    while (this.cards.length <= i) {
      const root = mk('div', 'lab-card', this.cardsEl);
      const name = mk('span', 'lab-card-name', root);
      const triWrap = mk('span', 'lab-card-tri', root);
      const tri = mk('span', 'n', triWrap);
      mk('span', 'u', triWrap).textContent = 'TRI';
      const sub = mk('span', 'lab-card-sub', root);
      this.cards.push({
        root, name, tri, sub,
        nameCache: '', triCache: '', subCache: '', over: false, hidden: false,
      });
    }
    return this.cards[i];
  }

  private commitSeed(): void {
    const raw = this.seedEl.value;
    if (raw === this.committed) return;
    this.committed = raw;
    this.onSeedChange(this.parseSeed(raw));
  }

  /** Digits are taken literally so a shared seed round-trips; anything else hashes. */
  private parseSeed(raw: string): number {
    const t = raw.trim();
    if (t.length === 0) return 0;
    if (DIGITS.test(t)) {
      const n = Number(t);
      if (n <= U32) return n >>> 0;
    }
    return hashString(t) >>> 0;
  }
}
